import {sendAnalyticsEvent} from '#/analytics/analytics';
import {LoadingDots} from '#/atoms/dots-loading-indicator';
import {LandsTable} from '#/components/lands-table/lands-table';
import {LandsGrid} from '#/components/lands/lands-grid';
import {useLandMarketInfos} from '#/hooks/query/use-land-market-info';
import {useLands} from '#/hooks/query/use-lands';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {DefaultChildren} from '#/utils/utils';
import {Bars3Icon, Squares2X2Icon} from '@heroicons/react/24/solid';
import * as Tabs from '@radix-ui/react-tabs';
import {motion} from 'framer-motion';
import {ComponentProps, FunctionComponent, createContext, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Land, LandMarketInfo} from 'shareland-common';

enum ExploreAndDiscoverTab {
  LIST = 'list',
  GRID = 'grid',
}

export const ExploreAndDiscover: FunctionComponent<DefaultChildren> = () => {
  const {t} = useTranslation();

  const {data: lands = [], isLoading} = useLands();
  const {data: landMarketInfos = []} = useLandMarketInfos(lands);
  const landAndMarketInfos = lands.map((land, index) => ({
    land,
    landMarketInfo: landMarketInfos[index],
  }));

  const {
    actionMenu: {openInformation},
  } = useQueryNavigationActions();

  const onLandClick = (land: Land, landMarketInfo: LandMarketInfo | undefined) => {
    openInformation(land);
    sendAnalyticsEvent.viewProperty({
      land,
      landMarketInfo,
    });
  };

  return (
    <TabsRoot className='px-5 md:px-10 @screen2xl/home:!px-homeMaxScreen2xl'>
      <div className='flex justify-between items-center pb-5 text-4xl z-10'>
        <h2 className='inline text-blue-600 font-bold pr-3'>{t('pages.home.allLocations.title')}</h2>
        <TabsList />
      </div>

      <Tabs.Content value={ExploreAndDiscoverTab.GRID}>
        {isLoading ? (
          <div className='w-full h-[400px] flex-center'>
            <LoadingDots />
          </div>
        ) : (
          <LandsGrid landAndInfos={landAndMarketInfos} onLandClick={onLandClick} />
        )}
      </Tabs.Content>

      <Tabs.Content className='w-full' value={ExploreAndDiscoverTab.LIST}>
        {isLoading ? (
          <div className='w-full h-[400px] flex-center'>
            <LoadingDots />
          </div>
        ) : (
          <LandsTable landAndInfos={landAndMarketInfos} onLandClick={onLandClick} />
        )}
      </Tabs.Content>
    </TabsRoot>
  );
};

interface ExploreAndDiscoverTabsContextValue {
  tab: string;
}

const initialValue: ExploreAndDiscoverTabsContextValue = {} as ExploreAndDiscoverTabsContextValue;
export const ExploreAndDiscoverTabsContext = createContext<ExploreAndDiscoverTabsContextValue>(initialValue);

export const TabsRoot: FunctionComponent<ComponentProps<typeof Tabs.Root>> = ({children, className, ...rest}) => {
  // Internal hooks

  const [tab, setTab] = useState<ExploreAndDiscoverTab>(ExploreAndDiscoverTab.GRID);
  const onValueChange = (value: string) => {
    setTab(value as ExploreAndDiscoverTab);
  };

  return (
    <Tabs.Root className={className} value={tab} onValueChange={onValueChange} {...rest}>
      <ExploreAndDiscoverTabsContext.Provider value={{tab}}>{children}</ExploreAndDiscoverTabsContext.Provider>
    </Tabs.Root>
  );
};

const TabsList: FunctionComponent = () => {
  const {tab} = useContext(ExploreAndDiscoverTabsContext);

  return (
    <Tabs.List className='bg-gray-200 p-2 rounded-lg'>
      <div className='flex items-stretch gap-1 relative w-full'>
        <motion.div
          variants={{
            grid: {left: 0, right: 'unset'},
            list: {left: 'unset', right: 0},
          }}
          initial={false}
          transition={{ease: 'easeInOut', duration: 0.2}}
          animate={tab}
          className='absolute h-full aspect-square bg-white rounded pointer-events-none'
        />
        <Tabs.Trigger
          value={ExploreAndDiscoverTab.GRID}
          className='p-0.5 relative border-0 aspect-square rounded bg-transparent hover:opacity-50'
          onClick={() => {
            sendAnalyticsEvent.exploreDataOrientation({data_orientation: 'grid'});
          }}
        >
          <Squares2X2Icon className='m-1 w-4 h-4' />
        </Tabs.Trigger>
        <Tabs.Trigger
          value={ExploreAndDiscoverTab.LIST}
          className='p-0.5 relative border-0 aspect-square rounded bg-transparent hover:opacity-50'
          onClick={() => {
            sendAnalyticsEvent.exploreDataOrientation({data_orientation: 'list'});
          }}
        >
          <Bars3Icon className='m-1 w-4 h-4' />
        </Tabs.Trigger>
      </div>
    </Tabs.List>
  );
};
